import { loadState, saveState } from 'utils/storage';

const normalizeTab = (tab) => {
	if (!tab || tab.displayedName) {
		return tab;
	}

	const fallback = tab.regnum || tab.name || '';

	if (!fallback) {
		return tab;
	}

	return {
		...tab,
		displayedName: fallback,
	};
};

/**
 * Removes duplicate tabs (by type) and ensures currentTab is valid.
 * @param {Object} tabsState { tabs: Array, current: Object }
 * @returns {Object} { tabs, current, changed: boolean }
 */
export function dedupeTabsState(tabsState) {
	if (!tabsState || !Array.isArray(tabsState.tabs)) {
		return {
			tabs: [],
			current: null,
			changed: false,
		};
	}

	const { tabs, current } = tabsState;
	const seen = new Set();
	const uniqueTabs = [];
	const tabByType = new Map();

	let changed = false;

	for (const tab of tabs) {
		const normalizedTab = normalizeTab(tab);

		if (normalizedTab !== tab) {
			changed = true;
		}

		if (!seen.has(normalizedTab.type)) {
			seen.add(normalizedTab.type);
			uniqueTabs.push(normalizedTab);
			tabByType.set(normalizedTab.type, normalizedTab);
		} else {
			changed = true;
		}
	}

	let nextCurrent = current;

	if (nextCurrent) {
		const normalizedCurrent = normalizeTab(nextCurrent);

		if (normalizedCurrent !== nextCurrent) {
			changed = true;
			nextCurrent = normalizedCurrent;
		}

		if (nextCurrent && tabByType.has(nextCurrent.type)) {
			const mappedCurrent = tabByType.get(nextCurrent.type);

			if (mappedCurrent !== nextCurrent) {
				nextCurrent = mappedCurrent;
				changed = true;
			}
		} else if (uniqueTabs.length) {
			nextCurrent = uniqueTabs[uniqueTabs.length - 1];
			changed = true;
		} else {
			nextCurrent = null;
			changed = true;
		}
	}

	if (!nextCurrent && uniqueTabs.length) {
		nextCurrent = uniqueTabs[uniqueTabs.length - 1];
		changed = true;
	}

	return {
		tabs: uniqueTabs,
		current: nextCurrent,
		changed,
	};
}

/**
 * Loads tabs from sessionStorage, deduplicates them and calls tabsLoadState.
 * tabsLoadState here is the already-bound action creator from connect.
 */
export function loadAndRestoreTabs(tabsLoadState) {
	const parsed = loadState('tabsState');

	if (!parsed || !Array.isArray(parsed.tabs)) {
		return;
	}

	const { tabs, current, changed } = dedupeTabsState(parsed);

	tabsLoadState({
		tabs,
		current,
	});

	if (changed) {
		saveState({
			tabs,
			current,
		}, 'tabsState');
	}
}
